import "vite/modulepreload-polyfill";

// UI fonts are always loaded; everything else is lazy-loaded.
import "@fontsource-variable/inter";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/400-italic.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource/ibm-plex-mono/600.css";
import "@fontsource/ibm-plex-mono/700.css";
import "@fontsource-variable/source-serif-4";

import "./index.css";

import * as Sentry from "@sentry/vue";
import { VueQueryPlugin } from "@tanstack/vue-query";
import { createApp } from "vue";

import Urls from "./autogen/urls";
import { createDepot } from "./lib/depot";
import router from "./router";
import AppWrapper from "./scaffolding/AppWrapper.vue";

const app = createApp(AppWrapper);

// Allow accessing URLs in all components.
app.config.globalProperties.Urls = Urls;

// Register third-party extensions.
{
  /** @type {import('@tanstack/vue-query').VueQueryPluginOptions} */
  const vueQueryPluginOptions = {
    queryClientConfig: {
      defaultOptions: {
        queries: {
          retry: false,
          gcTime: 5 * 60_000, // 5 minutes
          staleTime: 30_000, // 30 seconds
        },
      },
    },
  };

  app.use(VueQueryPlugin, vueQueryPluginOptions);
}

app.use(createDepot());
app.use(router);

// Enable Sentry.
// Via https://github.com/getsentry/sentry-javascript/issues/2925.
Sentry.init({
  app,
  dsn: "https://8022d4e4bf8f49c6bc1db715c70ec0ce@o97520.ingest.sentry.io/213667",
  integrations: [
    Sentry.browserTracingIntegration({
      router,

      // Otherwise, our bill gets pretty spicy.
      tracesSampleRate: 0.1,

      tracingOrigins: [
        "127.0.0.1",
        "buttondown.email",
        "buttondown.com",
        /^\//,
      ],
    }),
  ],

  // 2024-04-23: We will almost certainly need to bump this down or remove it entirely,
  // but let's see how replays work!
  replaysSessionSampleRate: 1.0,

  debug: DEBUG === "True",
  enabled: DEBUG !== "True",

  tracesSampleRate: 0.1,

  logErrors: true,
  trackComponents: true,
  ignoreErrors: [
    "Non-Error",
    "Can't find variable: pktAnnotationHighlighter",
    "ChunkLoadError",

    // We abort a lot of things to de-dupe requests, so this is fine.
    "AbortError",

    // Safari is incorrectly failing to parse some JSON+LD metadata. This is a known issue in Safari:
    // https://github.com/getsentry/sentry/issues/61469
    // Here's an example of such an issue:
    // https://buttondown-email.sentry.io/issues/5420885923/
    '"telephone"',
  ],
  initialScope: {
    user: {
      username: ACCOUNT.username,
      email: ACCOUNT.email_address,
      id: ACCOUNT.id,
    },
  },
});

app.mount("#app-scaffold");

// Via https://vitejs.dev/guide/build#load-error-handling,
// which itself is via https://github.com/vitejs/vite/issues/11804#issuecomment-2055217776,
// which is meant to (hopefully!) solve https://github.com/buttondown/roadmap/issues/2593.
window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});

export default app;
